import React, { useState, useEffect } from "react";
import Modal from "components/modal";

const DroitParticipationFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialDroit, 
  isEditing,
  existingDroits = [],
  loading 
}) => {
  const [droit, setDroit] = useState({
    name_droit: "",
    montant_droit: "",
    status_droit: true
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && initialDroit) {
      setDroit({
        name_droit: initialDroit.name_droit || "",
        montant_droit: initialDroit.montant_droit || "",
        status_droit: initialDroit.status_droit !== undefined ? initialDroit.status_droit : true,
      });
      setValidationErrors({});
    }
  }, [isOpen, initialDroit]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "montant_droit") {
      // Ne permettre que des nombres et un seul point décimal
      const regex = /^[0-9]*\.?[0-9]*$/;
      if (value === '' || regex.test(value)) {
        setDroit({
          ...droit,
          [name]: value
        });
      }
    } else {
      setDroit({
        ...droit,
        [name]: type === "checkbox" ? checked : value,
      });
    }

    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null,
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!droit.name_droit.trim()) {
      errors.name_droit = "Le nom du droit est requis";
    } else if (
      !isEditing &&
      existingDroits.some(d => 
        d.name_droit.toLowerCase() === droit.name_droit.toLowerCase()
      )
    ) {
      errors.name_droit = "Ce nom de droit existe déjà";
    }

    if (!droit.montant_droit || droit.montant_droit === "0") {
      errors.montant_droit = "Le montant du droit est requis et doit être supérieur à 0";
    } else if (isNaN(parseFloat(droit.montant_droit))) {
      errors.montant_droit = "Le montant doit être un nombre valide";
    } else if (parseFloat(droit.montant_droit) < 0) {
      errors.montant_droit = "Le montant ne peut pas être négatif";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    
    const result = await onSubmit({
      name_droit: droit.name_droit,
      montant_droit: droit.montant_droit,
      status_droit: droit.status_droit,
    });
    
    if (!result.success) {
      setValidationErrors(result.errors || {});
    }
    
    setSubmitting(false);
  };

  // Fonction pour formater le montant en devise (MGA) lors de l'affichage uniquement
  const formatCurrency = (amount) => {
    if (!amount) return "";
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return "";
    
    return new Intl.NumberFormat('fr-MG', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="md">
      <div className="text-center sm:text-left">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            {isEditing
              ? "Modifier le droit de participation"
              : "Créer un nouveau droit de participation"}
          </h3>
          <div className="h-1 w-12 bg-[#57A863] mt-2 rounded-full"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="name_droit"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Nom du droit
            </label>
            <input
              type="text"
              name="name_droit"
              id="name_droit"
              value={droit.name_droit}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#57A863] focus:outline-none focus:ring-[#57A863]"
              placeholder="Ex: Droit d'adhésion, Frais d'inscription..."
            />
            {validationErrors.name_droit && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.name_droit}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="montant_droit"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Montant (MGA)
            </label>
            <div className="relative mt-1 rounded-md shadow-sm">
              <input
                type="text"
                name="montant_droit"
                id="montant_droit"
                value={droit.montant_droit}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#57A863] focus:outline-none focus:ring-[#57A863]"
                placeholder="Ex: 10000"
                inputMode="decimal"
              />
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <span className="text-gray-500 sm:text-sm">Ar</span>
              </div>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Montant formaté: {formatCurrency(droit.montant_droit)} Ar
            </p>
            {validationErrors.montant_droit && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.montant_droit}
              </p>
            )}
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="status_droit"
                name="status_droit"
                type="checkbox"
                checked={droit.status_droit}
                onChange={handleInputChange}
                className="h-4 w-4 rounded border-gray-300 text-[#57A863] focus:ring-[#57A863]"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="status_droit" className="font-medium text-gray-700">
                Droit actif
              </label>
              <p className="text-gray-500">
                Un droit inactif ne sera pas disponible lors de la création des paiements.
              </p>
            </div>
          </div>

          {validationErrors.general && (
            <div className="rounded-md bg-red-50 p-3 border border-red-200">
              <p className="text-sm text-red-600">
                {validationErrors.general}
              </p>
            </div>
          )}

          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 rounded-md text-gray-600 border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
              onClick={onClose}
              disabled={submitting || loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-[#57A863] text-white hover:bg-[#4c9657] transition-colors shadow-sm"
              disabled={submitting || loading}
            >
              {(submitting || loading) ? (
                <span className="flex items-center">
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Chargement...
                </span>
              ) : isEditing ? (
                "Mettre à jour"
              ) : (
                "Créer"
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default DroitParticipationFormModal;